import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal, StaggerGroup, StaggerItem } from "@/components/shared/SectionReveal";
import { DownloadButton } from "@/components/shared/DownloadButton";

export default function Pricing() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const plans = t("pricing.plans", { returnObjects: true });

  return (
    <div data-testid="pricing-page">
      <SEO title={`${t("pricing.hero_title")} — Fastority`} description={t("pricing.hero_subtitle")} path="pricing" />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("pricing.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70">{t("pricing.hero_subtitle")}</p>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-offwhite -mt-1">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <StaggerGroup className="grid md:grid-cols-3 gap-6 items-start">
            {plans.map((plan, idx) => (
              <StaggerItem key={plan.name}>
                <div
                  data-testid={`pricing-plan-${plan.name}`}
                  className={`rounded-3xl p-8 h-full border relative ${
                    plan.featured
                      ? "bg-slate900 text-white border-slate900 shadow-2xl scale-[1.03]"
                      : "bg-white text-slate900 border-gray-100 shadow-[0_8px_30px_rgba(30,58,138,0.06)]"
                  }`}
                >
                  {plan.featured && (
                    <span className="absolute -top-3 right-8 bg-coral text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                  <h3 className="font-heading font-bold text-2xl mb-1">{plan.name}</h3>
                  <p className={`text-sm mb-6 ${plan.featured ? "text-white/60" : "text-slate-500"}`}>{plan.desc}</p>
                  {plan.price_lines ? (
                    <div className="mb-6 space-y-1">
                      {plan.price_lines.map((pl) => (
                        <div key={pl.label} className="flex items-baseline gap-2 flex-wrap" data-testid={`pricing-price-line-${plan.name}`}>
                          <span className="font-heading font-extrabold text-3xl">{pl.amount}</span>
                          <span className={`text-sm font-medium ${plan.featured ? "text-white/50" : "text-slate-400"}`}>{pl.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="font-heading font-extrabold text-4xl mb-6">
                      {plan.price}
                      {idx === 1 ? (
                        <span className={`text-sm font-medium ${plan.featured ? "text-white/50" : "text-slate-400"}`}> /{t("pricing.monthly")}</span>
                      ) : null}
                    </div>
                  )}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check size={16} className={plan.featured ? "text-emeraldgreen mt-0.5 shrink-0" : "text-deepblue mt-0.5 shrink-0"} />
                        <span className={plan.featured ? "text-white/85" : "text-slate-600"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.name === "PRO" ? (
                    <Link to={`/${lang}/pro`} data-testid={`pricing-cta-${plan.name}`} className="block text-center bg-coral text-white font-semibold px-6 py-3.5 rounded-full hover:scale-[1.02] transition-transform duration-300">
                      {plan.cta}
                    </Link>
                  ) : (
                    <DownloadButton
                      testId={`pricing-cta-${plan.name}`}
                      className={`block w-full text-center font-semibold px-6 py-3.5 rounded-full transition-transform duration-300 hover:scale-[1.02] ${
                        plan.featured ? "bg-coral text-white" : "bg-slate900 text-white"
                      }`}
                    >
                      {plan.cta}
                    </DownloadButton>
                  )}
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
}
